import gsap from "gsap";
import { celebrationTimeline } from "../rendering/celebration/celebrationTimeline";
import { firstSnowScore, getClimaxPosition, shouldRepeatPrelude } from "./soundtrackScore";
import { soundtrackMix } from "./soundtrackMix";

export type PlaybackState = "idle" | "playing" | "paused" | "blocked";

/** One streaming voice, with separate user, scene and edit envelopes. No full-track PCM allocation. */
export function createSoundtrackPlayer(audio: HTMLAudioElement, onState: (state: PlaybackState) => void) {
  const mix = { user: 0, scene: soundtrackMix.cinematic as number, envelope: 0 };
  let context: AudioContext | undefined;
  let gain: GainNode | undefined;
  let source: MediaElementAudioSourceNode | undefined;
  let graphAttempted = false;
  let stage: "ambient" | "silence" | "climax" | "outro" = "ambient";
  let started = false, enabled = false, hidden = false, disposed = false, ended = false, looping = false;
  let revision = 0, elapsed = 0;
  let playbackRequest = 0, mediaReady = false;
  let reportedState: PlaybackState = "idle";
  let pendingSeek: number | undefined;
  audio.volume = 0;
  audio.loop = false;

  const applyVolume = () => {
    const volume = Math.min(1, Math.max(0, mix.user * mix.scene * mix.envelope));
    if (context && gain) gain.gain.setTargetAtTime(volume, context.currentTime, 0.015);
    else audio.volume = volume;
  };
  const fade = (key: keyof typeof mix, value: number, duration: number, onComplete?: () => void) => {
    gsap.killTweensOf(mix, key);
    const tween = gsap.to(mix, { [key]: value, duration, ease: "sine.inOut", onUpdate: applyVolume, onComplete });
    if (hidden) tween.pause();
    return tween;
  };
  const seek = (position: number) => {
    pendingSeek = position;
    if (audio.readyState < 1) return;
    audio.currentTime = Math.min(position, Number.isFinite(audio.duration) ? Math.max(0, audio.duration - 0.05) : position);
    pendingSeek = undefined;
  };
  const metadata = () => { if (pendingSeek !== undefined) seek(pendingSeek); };
  const report = (state: PlaybackState) => { reportedState = state; onState(state); };
  const outputChanged = () => {
    if (disposed || hidden || !started || !enabled) return;
    if (context?.state !== "running") report("blocked");
    else if (mediaReady && !audio.paused) report("playing");
  };

  function unlockGraph(): Promise<boolean> {
    mediaReady = false;
    if (!graphAttempted) {
      graphAttempted = true;
      const Context = window.AudioContext ?? (window as unknown as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (Context) {
        try {
          context = new Context();
          gain = context.createGain();
          gain.gain.value = 0;
          source = context.createMediaElementSource(audio);
          source.connect(gain).connect(context.destination);
          context.addEventListener("statechange", outputChanged);
          audio.volume = 1; // GainNode also works on Safari, where media.volume is not writable.
        } catch {
          context?.removeEventListener("statechange", outputChanged);
          source?.disconnect();
          void context?.close().catch(() => undefined);
          source = undefined; context = undefined; gain = undefined;
        }
      }
    }
    applyVolume();
    if (!context) return Promise.resolve(true);
    outputChanged();
    const output = context;
    // Invoke in the original tap, not after awaiting media playback (user activation).
    try { return output.resume().then(() => output.state === "running", () => false); }
    catch { return Promise.resolve(false); }
  }

  async function play(outputReady?: Promise<boolean>) {
    if (disposed || hidden || !started || ended) return;
    const ticket = revision;
    const request = ++playbackRequest;
    // The score's intentional pause still allows enabling sound for the next burst.
    if (stage === "silence") {
      if (outputReady) {
        const ready = await outputReady;
        if (!disposed && !hidden && enabled && ticket === revision && request === playbackRequest)
          report(ready ? "playing" : "blocked");
      }
      return;
    }
    mediaReady = false;
    try {
      await audio.play();
      const ready = outputReady ? await outputReady : !context || context.state === "running";
      if (disposed || hidden || !started) { audio.pause(); return; }
      if (ticket !== revision || request !== playbackRequest) return;
      if (!ready || (context && context.state !== "running")) throw new Error("Audio output is suspended");
      mediaReady = true;
      if (enabled) report("playing");
    } catch {
      if (disposed || hidden || ticket !== revision || request !== playbackRequest || !enabled) return;
      mediaReady = false;
      enabled = false;
      gsap.killTweensOf(mix, "user");
      mix.user = 0;
      applyVolume();
      report("blocked");
    }
  }

  function repeatPrelude() {
    if (disposed || !started || hidden || stage !== "ambient" || looping) return;
    if (!ended && !shouldRepeatPrelude(audio.currentTime)) return;
    looping = true;
    const ticket = ++revision;
    fade("envelope", 0, firstSnowScore.boundaryFade, () => {
      if (disposed || ticket !== revision || stage !== "ambient") return;
      seek(firstSnowScore.preludeStart);
      ended = false;
      void play().then(() => {
        if (disposed || ticket !== revision) return;
        looping = false;
        fade("envelope", 1, 1.2);
      });
    });
  }
  const mediaEnded = () => { ended = true; repeatPrelude(); };
  const mediaError = () => {
    if (!started || disposed) return;
    playbackRequest += 1; mediaReady = false;
    enabled = false;
    gsap.killTweensOf(mix, "user");
    mix.user = 0;
    applyVolume();
    report("blocked");
  };
  audio.addEventListener("loadedmetadata", metadata);
  audio.addEventListener("timeupdate", repeatPrelude);
  audio.addEventListener("ended", mediaEnded);
  audio.addEventListener("error", mediaError);

  const player = {
    async start() {
      if (disposed) return;
      if (!started) { seek(firstSnowScore.openingStart); stage = "ambient"; ended = false; }
      started = true; enabled = true;
      const outputReady = unlockGraph();
      fade("user", 1, 0.7);
      fade("envelope", 1, 2);
      await play(outputReady);
    },
    async toggle() {
      if (disposed) return;
      if (enabled && reportedState !== "blocked") {
        playbackRequest += 1;
        enabled = false;
        fade("user", 0, 0.25);
        report("paused");
        // Keep the silent playhead in sync with the visible show.
      } else {
        if (!started) { await player.start(); return; }
        enabled = true;
        const outputReady = unlockGraph();
        fade("user", 1, 0.6);
        await play(outputReady);
      }
    },
    setSceneVolume(volume: number) {
      if (stage === "ambient") fade("scene", volume, 0.9);
    },
    syncCelebration(time: number) {
      if (disposed) return;
      if (stage === "ambient" || time < elapsed || (time === 0 && stage === "outro")) {
        revision += 1; stage = "silence"; looping = false; ended = false;
        const ticket = revision;
        fade("envelope", 0, 0.28, () => { if (ticket === revision) audio.pause(); });
      }
      elapsed = time;
      if (stage !== "silence" || time < celebrationTimeline.firstBurst) return;
      revision += 1; stage = "climax";
      seek(getClimaxPosition(time));
      fade("scene", soundtrackMix.peak, 1.8);
      void play().then(() => {
        if (disposed || stage !== "climax" || hidden) return;
        // Slow loading must not leave music several seconds behind the fireworks.
        if (Math.abs(audio.currentTime - getClimaxPosition(elapsed)) > 0.75) seek(getClimaxPosition(elapsed));
        fade("envelope", 1, 1.4);
      });
    },
    finish() {
      if (disposed) return;
      stage = "outro";
      revision += 1;
      // Leave the listener's chosen passage running under the final title.
      fade("scene", soundtrackMix.finale, 8);
      void play();
      fade("envelope", 1, 2);
    },
    async setHidden(value: boolean) {
      if (disposed) return;
      hidden = value;
      gsap.getTweensOf(mix).forEach(tween => value ? tween.pause() : tween.resume());
      if (value) {
        playbackRequest += 1; mediaReady = false;
        audio.pause(); void context?.suspend().catch(() => undefined);
      }
      else {
        if (started && enabled) report("blocked");
        const outputReady = started ? unlockGraph() : undefined;
        const playback = play(outputReady), request = playbackRequest;
        await playback;
        if (!disposed && !hidden && request === playbackRequest && stage === "climax") fade("envelope", 1, 0.8);
      }
    },
    reset() {
      revision += 1;
      playbackRequest += 1; mediaReady = false;
      started = false; enabled = false; ended = false; looping = false; stage = "ambient"; elapsed = 0;
      gsap.killTweensOf(mix);
      audio.pause();
      mix.user = 0; mix.envelope = 0; mix.scene = soundtrackMix.cinematic;
      applyVolume();
      seek(firstSnowScore.openingStart);
      report("idle");
    },
    dispose() {
      disposed = true; revision += 1; started = false;
      playbackRequest += 1; mediaReady = false;
      gsap.killTweensOf(mix);
      audio.pause();
      audio.removeEventListener("loadedmetadata", metadata);
      audio.removeEventListener("timeupdate", repeatPrelude);
      audio.removeEventListener("ended", mediaEnded);
      audio.removeEventListener("error", mediaError);
      context?.removeEventListener("statechange", outputChanged);
      source?.disconnect(); gain?.disconnect();
      void context?.close().catch(() => undefined);
    },
  };
  return player;
}

export type SoundtrackPlayer = ReturnType<typeof createSoundtrackPlayer>;
